import {
  type FreeLayoutPluginContext,
  Rectangle,
  TransformData,
} from '@flowgram.ai/free-layout-editor';
import { nodesIntoView } from '$/hooks/useFitView';
import useTeamAgentStore from '$/store/useTeamAgentStore';

interface FocusableFlowNode {
  getData: (key: any) => unknown;
  transform?: {
    bounds?: Rectangle;
  };
}

interface FocusNodeViewportOptions {
  padding?: number;
  easingDuration?: number;
}

/**
 * 将指定节点滚动到画布中心，用于节点状态切换后保持当前操作目标明确。
 */
export function focusNodeViewport(
  ctx: FreeLayoutPluginContext,
  node: FocusableFlowNode,
  options?: FocusNodeViewportOptions,
): void {
  requestAnimationFrame(() => {
    const bounds = (node.getData(TransformData) as TransformData | undefined)?.bounds;
    if (!bounds) {
      return;
    }

    const padding = options?.padding ?? 80;
    ctx.playground.config.scrollToView({
      bounds: new Rectangle(
        bounds.x - padding,
        bounds.y - padding,
        bounds.width + padding * 2,
        bounds.height + padding * 2,
      ),
      zoom: ctx.playground.config.zoom,
      scrollToCenter: true,
      easing: true,
      easingDuration: options?.easingDuration ?? 300,
    });
  });
}

interface FocusNodeIntoViewOptions {
  /** 为 true 时按较长边占视口 80% 计算缩放，对齐旧版 Agent Teams 双击行为 */
  fitZoom?: boolean;
}

/**
 * 将节点聚焦到视口中心，可选放大至合适比例（双击节点场景）。
 */
export function focusNodeIntoView(
  ctx: FreeLayoutPluginContext,
  node: FocusableFlowNode,
  options?: FocusNodeIntoViewOptions,
): void {
  const bounds = node.transform?.bounds
    ?? (node.getData(TransformData) as TransformData | undefined)?.bounds;
  if (!bounds) {
    return;
  }

  const { sideBarWidth, sideBarCollapsed } = useTeamAgentStore.getState();
  nodesIntoView({
    ctx,
    bounds,
    fitZoom: options?.fitZoom ?? true,
    sidebarWidth: sideBarWidth,
    sideBarCollapsed,
  });
}
